<template>
    <div class="card mb-0 pt-0" v-if="simulations">

        <p class="card-body mt-2" v-if="empty">
            <strong>{{ $t('dashboard.no-simulation') }}</strong>
        </p>

        <div v-if="!empty">

            <el-table class="table-responsive table-flush"
                    header-row-class-name="thead-light"
                    :data="simulations">

                <el-table-column width="130" >
                    <div slot="header">
                        {{ $t('common.simulation') }}
                    </div>
                    <template v-slot="{row}">

                        <h3 class="mb-0">
                            <a href="#" @click.prevent="openSimulation(row)"
                                v-if="row.session && (row.state.published || $permissions.hasRight('dashboard.learners.write'))">
                                {{ row.name }}
                            </a>
                            <span v-else>
                                {{ row.name }}
                            </span>
                            <small v-if="row.remedial">
                                <i class="fas fa-undo ml-1"></i>
                            </small>
                            <i class="fas fa-clipboard-check ml-1" v-if="row.assessment"></i>
                        </h3>

                        <div class="pt-1">
                            <small class="d-block"
                                v-if="row.state.published && position(row)"
                            >
                                <i class="fas fa-sync-alt mr-1"></i> {{ position(row) }}
                            </small>

                            <small class="d-block"
                                v-if="row.state.published && teammate(row) && $permissions.hasRight('dashboard.learners.read')"
                            >
                                <i class="fas fa-user-friends mr-1"></i> {{ teammate(row).name }}
                            </small>

                            <small v-if="row.state.published" class="d-block">
                                <i class="fas fa-pencil-alt mr-1"></i> {{ row.state.instructor_name }}
                            </small>

                            <small v-if="row.state.acknowledged" class="d-block">
                                <i class="fas fa-check mr-1"></i> {{ learner.name }}
                            </small>
                        </div>

                    </template>
                </el-table-column>

                <el-table-column min-width="300">
                    <div slot="header">
                        {{ $t('dashboard.comment') }}
                    </div>
                    <template v-slot="{row}">

                        <!-- Keep it on the same line -->
                        <p class="comment" v-if="row.state.published">{{ row.state.comment }}</p>

                    </template>
                </el-table-column>
               
                <el-table-column v-for="(competency, index) in sortedCompetencies" :key="index"
                    align="center" header-align="center"
                    :class-name="competencyClass(competency)" width="50"
                >
                    <div slot="header">
                        <div class="vertical pt-2 pb-2">{{ competencyName(competency) }}</div>
                    </div>

                    <template v-slot="{row}">
                        <rating v-if="row.state.published && row.state.competencies && row.state.competencies[simulationRole]"
                            :name="competencyName(competency)"
                            :description="competencyDescription(competency)"
                            :value="row.state.competencies[simulationRole][competency.id]"
                            :options="ratingOptions"
                            :comment="competencyComment(row.state, simulationRole, competency.id)"
                        >
                        </rating>
                    </template>

                </el-table-column>
                
                <el-table-column width="10">
                </el-table-column>

            </el-table>
        </div>
    </div>
</template>

<script>
    import moment from 'moment'
    import Rating from './Rating'

    export default {

        components: {
            Rating
        },

        props: {

            num: {
                type: Number,
            },

            learner: {
                type: Object,
                required: true
            },

            registration: {
                type: Object,
            },

            simulations: {
                type: Array,
            },

            competencies: {
                type: Object,
            },

            position1Competencies: {
                type: Object,
            },

            position2Competencies: {
                type: Object,
            },

            ratingOptions: {
                type: Array,
            },
        },
        
        data() {
            return {
                moment: moment,
            }
        },

        computed: {
            empty() {
                return this.simulations.length == 0
            },

            simulationRole() {
                return this.registration.degree['simulation_role_' + this.num]
            },

            sortedCompetencies() {
                return Object.values(this['position' + this.num + 'Competencies']).map(competency => this.competencies[competency.id])
                    .sort((a, b) => a.position - b.position)
            }
        },

        methods: {

            competencyName(competency) {
                const nameProp = 'name_' + this.registration.degree.lang
                return competency[nameProp]
            },

            competencyColor(competency) {
                return this.competencies[competency.id].domain.color
            },

            competencyClass(competency) {
                return 'rating border-' + this.competencyColor(competency)
            },

            competencyDescription(competency) {
                const posCompetency = this['position' + this.num + 'Competencies'][competency.id]
                if (!posCompetency) {
                    return ''
                }
                return posCompetency.pivot.description
            },

            competencyComment(state, simulationRole, competencyId) {
                return state.competency_comments && state.competency_comments[simulationRole]
                    ? state.competency_comments[simulationRole][competencyId]
                    : null
            },

            date(simulation) {
                if (simulation.session && simulation.session.started_at) {
                    return this.moment(simulation.session.started_at).format('YYYY/MM/DD')
                } else {
                    return this.moment(simulation.state.date).format('YYYY/MM/DD')
                }
            },
            
            teammate(simulation) {
                if (!simulation.session) {
                    return false
                }
                if (this.learner.id == simulation.session.learner1_id && simulation.session.learner2) {
                    return simulation.session.learner2
                }
                if (this.learner.id == simulation.session.learner2_id && simulation.session.learner1) {
                    return simulation.session.learner1
                }
                if (simulation.session.teammate) {
                    return simulation.session.teammate
                }
                return null
            },

            position(simulation) {
                if (!simulation.session) {
                    return false
                }
                if (this.learner.id == simulation.session.learner1_id) {
                    let position = this.registration.degree.simulation_role_1_name[this.registration.degree.lang];
                    if (this.registration.degree.simulation_role_2) {
                        position += ' / ' + this.registration.degree.simulation_role_2_name[this.registration.degree.lang];
                    }
                    return position
                }
                if (this.learner.id == simulation.session.learner2_id) {
                    let position = this.registration.degree.simulation_role_2_name[this.registration.degree.lang];
                    if (this.registration.degree.simulation_role_1) {
                        position += ' / ' + this.registration.degree.simulation_role_1_name[this.registration.degree.lang];
                    }
                    return position
                }
                return false
            },

            openSimulation(simulation) {
                let routeName = simulation.state.published 
                    ? 'dashboard-learner-simulation'
                    : 'dashboard-learner-simulation-edit'

                this.$router.push({
                    name: routeName,
                    params: {
                        learnerId: this.$route.params.learnerId,
                        registrationId: this.$route.params.registrationId,
                        simulationId: simulation.id,
                    }
                });
            },
        }
    }
</script>

<style scoped>

    .card >>> td {
        padding: 1rem 0 1rem 1rem;
        font-size: 0.8rem;
    }
    .card >>> th.rating {
        padding-left: 1rem;
        padding-right: 0;
        border-width: 0;
        border-bottom-width: medium;
    }
    .card >>> td.rating {
        padding-left: 0;
        padding-right: 0;
        border-width: 0;
    }
    .card >>> .vertical {
        writing-mode: vertical-rl;
        transform: rotate(180deg);
    }
    .card >>> .border-3 {
        border-width:3px !important;
    }
    .card >>> .comment {
        white-space: pre-wrap;
        font-size: 100%;
    }
    .badge.badge-success {
        background-color: #1aae6f;
        color: #fff;
        font-size: 0.7rem;
    }
    
</style>